import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/States';
import { CHART_SERIES } from '../../../components/ui/chart';
import type { BreakdownItem, ContractRow, MrrBridge } from '../../../api/finance';
import { odooContractText } from './customerLabels';
import { overrideConflictSentence } from './overrideCheck';
import { money, planDisplayName, signedMoney } from './formatters';
import { PanelTitle } from './DataIssues';

/**
 * MRR by billing term, cut from the same contracts the headline MRR counts,
 * then bridged to it: an override from the MRR sheet replaces a customer's
 * Odoo amount and belongs to no billing term, so it is its own line under the
 * terms rather than an unexplained difference.
 */
export function PlanBreakdownCard({
  items,
  currency,
  bridge,
  contracts = [],
}: {
  items: BreakdownItem[];
  currency: string;
  bridge: MrrBridge;
  /** For the override lines' Odoo amount in the order's currency on API builds that do not send it. */
  contracts?: ContractRow[];
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const max = Math.max(1, ...items.map((i) => i.mrr));

  const toggle = (label: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  return (
    <Card padding="md">
      <PanelTitle
        title="MRR by billing term"
        info="The contracts counted in this month's MRR, grouped by their Odoo billing term (monthly, 6 months, yearly…), at their Odoo amounts with any setup fee spread over the term. Reporting overrides from the MRR sheet replace a customer's Odoo amount; they belong to no term, so they are listed under the terms. A few customers are listed under yearly although Odoo carries a shorter plan (Xerox, ISS UK, Embross, Arendt); their rows say so, and only the row changes, never the amount. Click a term for its customers."
      />
      {items.length === 0 ? (
        <EmptyState compact title="No active subscriptions" />
      ) : (
        <ul className="space-y-2.5">
          {items.map((i) => {
            const isOpen = expanded.has(i.label);
            return (
              <li key={i.label}>
                <button type="button" className="w-full text-left cursor-pointer" onClick={() => toggle(i.label)}>
                  <div className="flex items-center justify-between text-body-xs text-text-secondary mb-1">
                    <span className="truncate mr-2 flex items-center gap-1">
                      <ChevronRight
                        className={`h-3.5 w-3.5 shrink-0 text-text-tertiary transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        aria-hidden
                      />
                      <span className="truncate" title={i.label}>{planDisplayName(i.label)}</span>
                    </span>
                    <span className="shrink-0 tabular-nums">
                      <span className="text-text-primary font-medium">{money(i.mrr, currency)}</span>{' '}
                      <span className="text-text-tertiary">· {i.count}</span>
                    </span>
                  </div>
                  {/* The kit MeterBar's look (thin ink bar, dashed remainder) at full row width. */}
                  <span className="relative flex h-1 w-full items-center" aria-hidden>
                    <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-border" />
                    <span
                      className="relative h-1 rounded-full"
                      style={{ width: `${(i.mrr / max) * 100}%`, backgroundColor: CHART_SERIES[0] }}
                    />
                  </span>
                </button>
                {isOpen && (
                  <ul className="mt-1.5 ml-4 divide-y divide-border-light">
                    {i.companies.map((c, idx) => (
                      <li
                        key={`${c.name}-${idx}`}
                        className="flex items-center justify-between gap-3 py-1.5 text-body-xs"
                      >
                        <span className="min-w-0">
                          <span className="block text-text-secondary truncate">{c.name}</span>
                          {c.odooName && (
                            <span className="block text-text-tertiary truncate">{c.odooName}</span>
                          )}
                          {c.termNote && <span className="block text-text-tertiary truncate" title={c.termNote}>{c.termNote}</span>}
                        </span>
                        <span className="text-text-primary shrink-0 tabular-nums">{money(c.mrr, currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <BridgeLines bridge={bridge} currency={currency} contracts={contracts} />
    </Card>
  );
}

/** "Billing terms €83,550 · ISS … −€2,495 · Swiss GRC −€625 · MRR €80,430". */
/**
 * The counted contract behind an override line in its own currency: the API's
 * when sent, else the one contract of that customer at that EUR amount.
 */
function contractSourceOf(
  o: MrrBridge['overrides'][number],
  contracts: ContractRow[],
): { mrr: number; currency: string } | null {
  if (o.contractSource !== undefined) return o.contractSource;
  const matches = contracts.filter(
    (c) =>
      (c.companyName === o.name || c.odooName === o.customer || c.companyName === o.customer) &&
      c.mrrEur !== null &&
      Math.abs(c.mrrEur - o.contractMrr) < 1,
  );
  return matches.length === 1 ? { mrr: matches[0]!.sourceMrr, currency: matches[0]!.sourceCurrency } : null;
}

function BridgeLines({ bridge, currency, contracts }: { bridge: MrrBridge; currency: string; contracts: ContractRow[] }) {
  if (bridge.overrides.length === 0 && Math.abs(bridge.unexplained) < 1) return null;
  return (
    <dl className="mt-4 border-t border-border-light pt-3 text-body-xs tabular-nums">
      <div className="flex justify-between gap-3 py-0.5">
        <dt className="text-text-secondary">Billing terms</dt>
        <dd className="text-text-primary">{money(bridge.byTermTotal, currency)}</dd>
      </div>
      {bridge.overrides.map((o) => (
        <div key={o.customer} className="flex justify-between gap-3 py-0.5">
          <dt className="min-w-0 text-text-secondary">
            <span className="block break-words" title={o.name !== o.customer ? `Odoo: ${o.customer}` : undefined}>
              Override: {o.name}
            </span>
            <span className="block break-words text-text-tertiary">
              {odooContractText(o.contractMrr, contractSourceOf(o, contracts)?.mrr, contractSourceOf(o, contracts)?.currency)} in
              Odoo → {money(o.reportedMrr, currency)} · {o.note}
            </span>
            {o.invoiceConflict && (
              <span className="mt-0.5 block break-words text-warning-dark">{overrideConflictSentence(o.invoiceConflict)}</span>
            )}
          </dt>
          <dd className="shrink-0 text-text-primary">{signedMoney(o.delta, currency)}</dd>
        </div>
      ))}
      {Math.abs(bridge.unexplained) >= 1 && (
        <div className="flex justify-between gap-3 py-0.5">
          <dt className="text-error-dark">Not explained (see the data note above)</dt>
          <dd className="shrink-0 text-error-dark">{signedMoney(bridge.unexplained, currency)}</dd>
        </div>
      )}
      <div className="mt-1 flex justify-between gap-3 border-t border-border-light pt-1.5 font-medium">
        <dt className="text-text-primary">MRR</dt>
        <dd className="text-text-primary">{money(bridge.total, currency)}</dd>
      </div>
    </dl>
  );
}
